import React, { useState, useEffect } from 'react';
import { X, Loader2 } from 'lucide-react';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle 
} from '@/components/ui/dialog';
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/utils';
import { useTheme } from '@/contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import AvatarSkeleton from '@/components/ui/avatar/AvatarSkeleton';

interface LikesModalProps {
  isOpen: boolean;
  onClose: () => void;
  postId?: string;
  commentId?: string;
  title?: string;
}

interface LikeUser {
  id: string;
  auth_id: string;
  full_name: string;
  avatar_url: string;
  city?: string;
}

const LikesModal: React.FC<LikesModalProps> = ({
  isOpen,
  onClose,
  postId,
  commentId,
  title = 'Likes'
}) => {
  const [users, setUsers] = useState<LikeUser[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen && (postId || commentId)) {
      fetchLikes();
    }
  }, [isOpen, postId, commentId]);

  const fetchLikes = async () => {
    if (!postId && !commentId) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      let query;
      
      if (postId) {
        // Fetch users who liked the post
        query = supabase
          .from('post_likes')
          .select(`
            user_id,
            users:user_id (
              id,
              auth_id,
              full_name,
              avatar_url,
              city
            )
          `)
          .eq('post_id', postId);
      } else if (commentId) {
        // Fetch users who liked the comment
        query = supabase
          .from('comment_likes')
          .select(`
            user_id,
            users:user_id (
              id,
              auth_id,
              full_name,
              avatar_url,
              city
            )
          `)
          .eq('comment_id', commentId);
      }

      const { data, error } = await query;
      
      if (error) throw error;
      
      // Transform data to get user objects
      const likeUsers = data
        .map(item => item.users)
        .filter(Boolean);
      
      setUsers(likeUsers);
    } catch (err) {
      console.error('Error fetching likes:', err);
      setError('Failed to load likes');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUserClick = (userId: string) => {
    onClose();
    navigate(`/dashboard/members/${userId}`);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className={cn(
          "sm:max-w-[425px] p-0 overflow-hidden",
          isDarkMode ? "bg-dark-200 border-dark-300" : "bg-white border-gray-200"
        )}
      >
        <DialogHeader className={cn(
          "px-6 py-4 border-b",
          isDarkMode ? "border-dark-300" : "border-gray-200"
        )}>
          <DialogTitle className={cn(
            "text-center text-lg font-semibold",
            isDarkMode ? "text-white" : "text-gray-900"
          )}>
            {title}
          </DialogTitle>
        </DialogHeader>
        
        <div className="max-h-[60vh] overflow-y-auto">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-12 space-y-4">
              <div className="space-y-4 w-full max-w-[250px]">
                {Array.from({ length: 5 }).map((_, index) => (
                  <div key={index} className="flex items-center space-x-3 px-6">
                    <div className="w-10 h-10 rounded-full bg-dark-300 animate-pulse" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-dark-300 rounded w-24 animate-pulse" />
                      <div className="h-3 bg-dark-300 rounded w-16 animate-pulse" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : error ? (
            <div className="text-center py-8 dark:text-red-400 light:text-red-600">
              {error}
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-8 dark:text-dark-800 light:text-gray-500">
              No likes yet
            </div>
          ) : (
            <ul className={cn(
              "divide-y",
              isDarkMode ? "divide-dark-300" : "divide-gray-200"
            )}>
              {users.map(user => (
                <li 
                  key={user.id}
                  className={cn(
                    "px-6 py-4 transition-colors cursor-pointer",
                    isDarkMode ? "hover:bg-dark-300/50" : "hover:bg-gray-100/50"
                  )}
                  onClick={() => handleUserClick(user.auth_id)}
                >
                  <div className="flex items-center space-x-3">
                    <img 
                      src={user.avatar_url || 'https://hebbkx1anhila5yf.public.blob.vercel-storage.com/gold_symbol-removebg-n5agUlyNamyXXN0FU8QGzScjug8DVS.png'} 
                      alt={user.full_name}
                      className="w-10 h-10 rounded-full object-cover dark:border-dark-400 light:border-gray-200 border shadow-md"
                    />
                    <div>
                      <div className="font-medium dark:text-white light:text-gray-900">{user.full_name}</div>
                      {user.city && (
                        <div className="text-dark-800 text-sm">{user.city}</div>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LikesModal;